import { Box } from "@mui/material";
import { memo, useEffect, useMemo, useState } from "react";
import { IVariablesExamplesProps } from "./models";
import { VariablesContainer } from "./VariablesContainer";
import { VariableField } from "./VariableField/VariableField";
import { REMOVE_CURLY_BRACKETS } from "../../../../../utils/conversions";

const normalize = (s?: string | null) =>
  (s ?? "").replace(REMOVE_CURLY_BRACKETS, "").trim();

export const VariablesExamples = memo(
  ({ variables, characterLimitRequests }: IVariablesExamplesProps) => {
    const [labelWidth, setLabelWidth] = useState(0);

    useEffect(() => {
      setLabelWidth(() => {
        let length = 0;
        for (let i = 0; i < variables.length; i++) {
          if (variables[i].length > length) length = variables[i].length;
        }
        return length * 8;
      });
    }, [variables]);

    const charIndexByVariable = useMemo(() => {
      const result: number[] = [];
      const used = new Set<number>();
      const pool =
        characterLimitRequests?.map((item, idx) => ({
          idx,
          norm: normalize(item?.name),
        })) ?? [];

      for (const variable of variables) {
        const vNorm = normalize(variable);
        let foundIdx: number | null = null;

        for (const { idx, norm } of pool) {
          if (!used.has(idx) && norm === vNorm) {
            foundIdx = idx;
            break;
          }
        }

        result.push(foundIdx ?? -1);
        if (foundIdx != null && foundIdx >= 0) used.add(foundIdx);
      }
      return result;
    }, [variables, characterLimitRequests]);

    const enabledIndexSet = useMemo(() => {
      const seen = new Set<string>();
      const enabled = new Set<number>();
      (characterLimitRequests ?? []).forEach((item, idx) => {
        const id = (item?.ticketAutomationMacroId ?? "").trim();
        if (!id) return;
        if (!seen.has(id)) {
          seen.add(id);
          enabled.add(idx);
        }
      });
      return enabled;
    }, [characterLimitRequests]);

    return (
      <VariablesContainer>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {variables &&
            variables.length > 0 &&
            variables.map((variable, index) => {
              const charIdx = charIndexByVariable[index];
              const editable =
                charIdx >= 0 ? enabledIndexSet.has(charIdx) : false;

              return (
                <VariableField
                  variable={variable}
                  index={index}
                  labelWidth={labelWidth}
                  characterIndex={charIdx}
                  editable={editable}
                  key={`${variable}-${charIdx}-${index}`}
                />
              );
            })}
        </Box>
      </VariablesContainer>
    );
  },
);
